import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { logoutUser } from './authService';
import { myFirebaseAuth } from '../../firebase/config';
import {
  AiNotificationReminderType,
  AiNotificationSettings,
  DocumentDelivery,
  EmailDelivery,
  DocumentDeliverySettings,
  AiNotificationTone,
  BuyerMonthlyAnalytics,
  BuyerUpdate,
  BuyerUserPermissions,
  DeliveryAddress,
} from '@dill/dill-shared';
import { firebaseFileUploadHelper } from '../../utils/helpers';

export const updateSupplierBuyer = createAsyncThunk(
  'supplierBuyers/updateSupplierBuyer',
  async (
    {
      supplierId,
      supplierBuyerDetails,
      supplierBuyerId,
    }: {
      supplierId: string;
      supplierBuyerDetails: BuyerUpdate;
      supplierBuyerId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/updateBuyer/${supplierBuyerId}`,
        { ...supplierBuyerDetails },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      // The server's reason when it gave one (e.g. Infor rejecting a credit hold change).
      return thunkApi.rejectWithValue(error?.response?.data?.errors?.[0]?.msg || error.message);
    }
  }
);

// Flags/unflags many buyers in one request - powers the Customers page's bulk "Flag"/"Unflag"
// selected-rows action. See bulkSetBuyerFlagService (backend) for the shared/team-wide semantics.
export const bulkSetBuyerFlag = createAsyncThunk(
  'supplierBuyers/bulkSetBuyerFlag',
  async (
    {
      supplierId,
      buyerIds,
      isFlagged,
    }: {
      supplierId: string;
      buyerIds: string[];
      isFlagged: boolean;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/bulkSetFlag`,
        { buyerIds, isFlagged },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateSupplierBuyerFiservFeeRate = createAsyncThunk(
  'supplierBuyers/updateSupplierBuyerFiservFeeRate',
  async (
    {
      supplierId,
      buyerId,
      fiservBuyerFeeRate,
    }: {
      supplierId: string;
      buyerId: string;
      fiservBuyerFeeRate: number;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/updateBuyerFiservFeeRate/${buyerId}`,
        { fiservBuyerFeeRate },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateBuyerEmailPreferences = createAsyncThunk(
  'supplierBuyers/updateBuyerEmailPreferences',
  async (
    {
      supplierId,
      buyerId,
      invoiceStatementEmailsOptOutUserIds,
      invoicePaymentRemindersEmailsOptOutUserIds,
      invoicePaymentReminderSettings,
      discountReminderSettings,
      creditLimitReminderSettings,
      isInvoiceNoticesDisabled,
      invoiceOptOutEmails,
      invoiceDeliverySettings,
      statementDeliverySettings,
      isPaymentRemindersDisabled,
      aiNotificationSettings,
    }: {
      supplierId: string;
      buyerId: string;
      invoiceStatementEmailsOptOutUserIds?: string[];
      invoicePaymentRemindersEmailsOptOutUserIds?: string[];
      invoicePaymentReminderSettings?: {
        numberOfDaysFromCurrentDateToDueDate?: number[];
        numberOfDaysFromCurrentDateToCreditHoldDate?: number[];
        isSendEmailReminders?: boolean;
        includeAttachments?: boolean;
        isManualReminderDeactivated?: boolean;
      };
      discountReminderSettings?: {
        isDiscountReminderEnabled: boolean;
        numberOfDaysBeforeDiscountDate: number[];
      };
      creditLimitReminderSettings?: {
        isCreditLimitReminderEnabled: boolean;
        creditLimitThresholdPercent: number;
      };
      isInvoiceNoticesDisabled?: boolean;
      invoiceOptOutEmails?: string[];
      invoiceDeliverySettings?: DocumentDeliverySettings;
      statementDeliverySettings?: DocumentDeliverySettings;
      isPaymentRemindersDisabled?: boolean;
      aiNotificationSettings?: AiNotificationSettings;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/updateEmailPreferences/${buyerId}`,
        {
          ...(invoiceStatementEmailsOptOutUserIds !== undefined && {
            invoiceStatementEmailsOptOutUserIds,
          }),
          ...(invoicePaymentRemindersEmailsOptOutUserIds !== undefined && {
            invoicePaymentRemindersEmailsOptOutUserIds,
          }),
          ...(invoicePaymentReminderSettings !== undefined && { invoicePaymentReminderSettings }),
          ...(discountReminderSettings !== undefined && { discountReminderSettings }),
          ...(creditLimitReminderSettings !== undefined && { creditLimitReminderSettings }),
          ...(isInvoiceNoticesDisabled !== undefined && { isInvoiceNoticesDisabled }),
          ...(invoiceOptOutEmails !== undefined && { invoiceOptOutEmails }),
          ...(invoiceDeliverySettings !== undefined && { invoiceDeliverySettings }),
          ...(statementDeliverySettings !== undefined && { statementDeliverySettings }),
          ...(isPaymentRemindersDisabled !== undefined && { isPaymentRemindersDisabled }),
          ...(aiNotificationSettings !== undefined && { aiNotificationSettings }),
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const generateAiNotificationRecommendation = createAsyncThunk(
  'supplierBuyers/generateAiNotificationRecommendation',
  async ({ supplierId, buyerId }: { supplierId: string; buyerId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<{
        recommendation: {
          dueDateOffsets: number[];
          creditHoldOffsets: number[];
          discountOffsets: number[];
          slots: AiNotificationSettings['slots'];
        };
      }>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/aiNotificationPreferences/recommend/${buyerId}`,
        {},
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getSupplierDocumentDeliveries = createAsyncThunk(
  'supplierBuyers/getSupplierDocumentDeliveries',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<{
        success: boolean;
        data: Omit<DocumentDelivery, 'pdfStoragePath' | 'idempotencyKey'>[];
      }>(`${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/documentDeliveries`, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

// Send Invoice: sends one invoice now by email / mail / fax to the given destinations.
export const sendInvoiceToCustomer = createAsyncThunk(
  'supplierBuyers/sendInvoiceToCustomer',
  async (
    {
      supplierId,
      buyerId,
      invoiceId,
      emails,
      mailingAddresses,
      faxNumbers,
    }: {
      supplierId: string;
      buyerId: string;
      invoiceId: string;
      emails: string[];
      mailingAddresses: DeliveryAddress[];
      faxNumbers: string[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<{
        success: boolean;
        data: { emailsSent: number; emailsFailed: number; physicalDeliveriesQueued: number };
      }>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/sendInvoice`,
        { buyerId, invoiceId, emails, mailingAddresses, faxNumbers },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error?.response?.data?.errors?.[0]?.msg || error.message);
    }
  }
);

// Invoice and statement emails, with what SendGrid reported for each (Document Delivery page).
export const getSupplierEmailDeliveries = createAsyncThunk(
  'supplierBuyers/getSupplierEmailDeliveries',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<{ success: boolean; data: EmailDelivery[] }>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/emailDeliveries`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const rewriteReminderTone = createAsyncThunk(
  'supplierBuyers/rewriteReminderTone',
  async (
    {
      supplierId,
      buyerId,
      reminderType,
      tone,
      currentSubject,
      currentNotes,
    }: {
      supplierId: string;
      buyerId: string;
      reminderType: AiNotificationReminderType;
      tone: AiNotificationTone;
      currentSubject?: string;
      currentNotes?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<{ subject: string; notes: string }>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/aiNotificationPreferences/rewriteTone/${buyerId}`,
        { reminderType, tone, currentSubject, currentNotes },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getSupplierBuyers = createAsyncThunk(
  'supplierBuyers/getSupplierBuyers',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/getBuyerByCompany`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const supplierUpdateBuyerUserPermissions = createAsyncThunk(
  'supplierBuyers/supplierUpdateBuyerUserPermissions',
  async (
    {
      supplierId,
      buyerId,
      userId,
      permissions,
    }: {
      supplierId: string;
      buyerId: string;
      userId: string;
      permissions: BuyerUserPermissions;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/updateUserPermissions/${buyerId}/${userId}`,
        { permissions },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const supplierDeleteBuyerUser = createAsyncThunk(
  'supplierBuyers/supplierDeleteBuyerUser',
  async (
    {
      supplierId,
      buyerId,
      userId,
    }: {
      supplierId: string;
      buyerId: string;
      userId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.delete<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/deleteUser/${buyerId}/${userId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getBuyerStatementPeriods = createAsyncThunk(
  'supplierBuyers/getBuyerStatementPeriods',
  async (
    {
      supplierId,
      buyerId,
    }: {
      supplierId: string;
      buyerId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/buyerStatementPeriods/${buyerId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const sendEmailStatementsForBuyersWithStatementId = createAsyncThunk(
  'supplierBuyers/sendEmailStatementsForBuyersWithStatementId',
  async (
    {
      buyerIds,
      statementId,
      supplierId,
      jobIds,
      jobNames,
      jobsSummary,
      additionalEmails,
      excludedUserIds,
      subjectLine,
      notes,
      attachments,
      isEmailEnabled,
      mailingAddresses,
      faxNumbers,
    }: {
      buyerIds: string[];
      statementId?: string;
      supplierId: string;
      jobIds?: string[];
      jobNames?: string[];
      jobsSummary?: {
        totalAmountDue: number;
        totalCurrentDue: number;
        totalPastDue1To30: number;
        totalPastDue31To60: number;
        totalPastDue61To90: number;
        totalPastDueOver90: number;
      };
      additionalEmails?: string[];
      excludedUserIds?: string[];
      subjectLine?: string;
      notes?: string;
      attachments?: File[];
      // One-off channels from Send Statement (mail / fax only for a single current statement).
      isEmailEnabled?: boolean;
      mailingAddresses?: DeliveryAddress[];
      faxNumbers?: string[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      // Upload attachments to Firebase if provided
      const uploadedAttachments: { url: string; name: string }[] = [];
      if (attachments && attachments.length > 0) {
        for (const attachment of attachments) {
          const blobUrl = URL.createObjectURL(attachment);
          const downloadUrl = await firebaseFileUploadHelper({
            blobUrl,
            fileNameWithExtension: `${new Date().getTime()}-${attachment.name}`,
            storagePath: `statement-emails/${supplierId}/${buyerIds[0] ?? 'bulk'}`,
          });
          if (downloadUrl) {
            uploadedAttachments.push({ url: downloadUrl, name: attachment.name });
          }
        }
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/buyersEmailStatement`,
        {
          buyerIds,
          statementId,
          ...(jobIds && jobIds.length > 0 ? { jobIds } : {}),
          ...(jobNames && jobNames.length > 0 ? { jobNames } : {}),
          ...(jobsSummary ? { jobsSummary } : {}),
          ...(additionalEmails && additionalEmails.length > 0 ? { additionalEmails } : {}),
          ...(excludedUserIds && excludedUserIds.length > 0 ? { excludedUserIds } : {}),
          ...(subjectLine ? { subjectLine } : {}),
          ...(notes ? { notes } : {}),
          ...(isEmailEnabled === false ? { isEmailEnabled } : {}),
          ...(mailingAddresses?.length ? { mailingAddresses } : {}),
          ...(faxNumbers?.length ? { faxNumbers } : {}),
          ...(uploadedAttachments.length > 0 ? { attachments: uploadedAttachments } : {}),
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const fetchSupplierBuyerMonthlyAnalytics = createAsyncThunk(
  'supplierBuyers/fetchSupplierBuyerMonthlyAnalytics',
  async (
    { supplierId, buyerId, months = 12 }: { supplierId: string; buyerId: string; months?: number },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) return null;
      const response = await axios.get<
        AppServerResponse & { data: { monthlyAnalytics: BuyerMonthlyAnalytics[] } }
      >(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierBuyers/buyerMonthlyAnalytics/${buyerId}?months=${months}`,
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
